import { useEffect, useState } from 'react';

type HealthKey = 'gateway' | 'auth' | 'timekeeper' | 'employee' | 'scheduler';

type HealthStatusValue = boolean | 'loading' | null;

type HealthStatus = Record<HealthKey, HealthStatusValue>;

const initialHealthStatus: HealthStatus = {
    gateway: null,
    auth: null,
    timekeeper: null,
    employee: null,
    scheduler: null,
};

const REQUEST_TIMEOUT_MS = 5000;

const getHealthEndpoints = (): Record<HealthKey, string> | null => {
    const gatewayDomain = process.env.REACT_APP_TK_GW_DOMAIN;

    if (!gatewayDomain) {
        return null;
    }

    return {
        gateway: `${gatewayDomain}/health`,
        auth: `${gatewayDomain}/v1/auth/health`,
        timekeeper: `${gatewayDomain}/v1/timekeeper/health`,
        employee: `${gatewayDomain}/v1/employee/health`,
        scheduler: `${gatewayDomain}/v1/schedule/health`,
    };
};

const fetchWithTimeout = async (url: string, timeoutMs: number): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
        return await fetch(url, {
            signal: controller.signal,
            cache: 'no-store',
        });
    } finally {
        clearTimeout(timeoutId);
    }
};

export const useGatewayHealthStatus = () => {
    const [healthStatus, setHealthStatus] = useState<HealthStatus>(initialHealthStatus);

    useEffect(() => {
        let active = true;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const checkHealthStatus = async (): Promise<HealthStatus> => {
            const endpoints = getHealthEndpoints();
            if (!endpoints) {
                const unavailable: HealthStatus = { ...initialHealthStatus };
                (Object.keys(unavailable) as HealthKey[]).forEach((key) => {
                    unavailable[key] = false;
                });
                return unavailable;
            }

            const keys = Object.keys(endpoints) as HealthKey[];

            setHealthStatus((prev) => {
                const loadingState = { ...prev };
                keys.forEach((key) => {
                    loadingState[key] = 'loading';
                });
                return loadingState;
            });

            const results = await Promise.allSettled(
                keys.map(async (key) => {
                    const response = await fetchWithTimeout(endpoints[key], REQUEST_TIMEOUT_MS);
                    return [key, response.ok] as const;
                }),
            );

            const nextStatus: HealthStatus = { ...initialHealthStatus };
            results.forEach((result, index) => {
                const key = keys[index];
                if (result.status === 'fulfilled') {
                    const [, value] = result.value;
                    nextStatus[key] = value;
                } else {
                    nextStatus[key] = false;
                }
            });

            return nextStatus;
        };

        const scheduleNextCheck = (nextStatus: HealthStatus) => {
            const allHealthy = Object.values(nextStatus).every((status) => status === true);
            const delay = allHealthy ? 30000 : 15000;
            timer = setTimeout(runChecks, delay);
        };

        const runChecks = async () => {
            const nextStatus = await checkHealthStatus();
            if (!active) {
                return;
            }
            setHealthStatus(nextStatus);
            scheduleNextCheck(nextStatus);
        };

        runChecks();

        return () => {
            active = false;
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, []);

    return healthStatus;
};
